import { compact, JsonLdGraph, JsonLdNode, JsonLdValue } from 'utils/jsonLd'

import {
  MEDLEVE_CONTACT_POINT,
  MEDLEVE_LANGUAGE,
  MEDLEVE_LEGAL_ENTITY,
  MEDLEVE_LOGO_PATH,
  MEDLEVE_ORGANIZATION_DESCRIPTION,
  MEDLEVE_SAME_AS,
  MEDLEVE_SITE_NAME
} from '../constants'
import { isPublishable } from '../publishable'
import { getDoctor, getMolecule } from '../registry'
import { BrandEntry, BreadcrumbItem, DoctorEntry, FaqItem, MoleculeEntry } from '../types'
import { absoluteUrl, canonicalUrl } from '../urls'
import {
  breadcrumbId,
  entityId,
  faqId,
  IdRef,
  organizationId,
  personId,
  profileId,
  ref,
  webPageId,
  websiteId
} from './ids'

export type { JsonLdGraph, JsonLdNode, JsonLdValue } from 'utils/jsonLd'
export { compact, serializeJsonLd } from 'utils/jsonLd'

/** Organization (PRD §51). `logo` and `contactPoint` only appear once real data is set in `constants.ts`. */
export const buildOrganization = (): JsonLdNode =>
  compact({
    '@type': 'Organization',
    '@id': organizationId(),
    name: MEDLEVE_SITE_NAME,
    legalName: MEDLEVE_LEGAL_ENTITY.legalName,
    taxID: MEDLEVE_LEGAL_ENTITY.cnpj,
    url: canonicalUrl('/'),
    logo: MEDLEVE_LOGO_PATH ? absoluteUrl(MEDLEVE_LOGO_PATH) : undefined,
    description: MEDLEVE_ORGANIZATION_DESCRIPTION,
    sameAs: [...MEDLEVE_SAME_AS],
    contactPoint: MEDLEVE_CONTACT_POINT
      ? compact({ '@type': 'ContactPoint', ...MEDLEVE_CONTACT_POINT, availableLanguage: MEDLEVE_LANGUAGE })
      : undefined
  })

/** WebSite (PRD §52). */
export const buildWebSite = (): JsonLdNode => ({
  '@type': 'WebSite',
  '@id': websiteId(),
  url: canonicalUrl('/'),
  name: MEDLEVE_SITE_NAME,
  publisher: ref(organizationId()),
  inLanguage: MEDLEVE_LANGUAGE
})

export interface WebPageInput {
  path: string
  name: string
  description?: string
  /** Adds `MedicalWebPage` to the type (PRD §43). Use only for predominantly medical pages. */
  medical?: boolean
  /** @id of the page's main entity (PRD §42/§49). An array only for pages with two subjects (comparisons). */
  about?: string | string[]
  /** @ids of secondary entities actually mentioned in the visible copy. Never use as a keyword list. */
  mentions?: string[]
  /** ISO dates of real events (PRD §44/§50). */
  datePublished?: string
  dateModified?: string
  lastReviewed?: string
  /** Doctor slugs. Unknown slugs are ignored. */
  reviewedBy?: string
  author?: string
  hasBreadcrumb?: boolean
}

const personRef = (slug?: string): IdRef | undefined => (slug && getDoctor(slug) ? ref(personId(slug)) : undefined)

/** WebPage / MedicalWebPage (PRD §43, §53). */
export const buildWebPage = ({
  path,
  name,
  description,
  medical = false,
  about,
  mentions = [],
  datePublished,
  dateModified,
  lastReviewed,
  reviewedBy,
  author,
  hasBreadcrumb = false
}: WebPageInput): JsonLdNode => {
  const aboutIds = about === undefined ? [] : Array.isArray(about) ? about : [about]

  return compact({
    '@type': medical ? ['WebPage', 'MedicalWebPage'] : 'WebPage',
    '@id': webPageId(path),
    url: canonicalUrl(path),
    name,
    description,
    inLanguage: MEDLEVE_LANGUAGE,
    isPartOf: ref(websiteId()),
    publisher: ref(organizationId()),
    about: aboutIds.length === 1 ? ref(aboutIds[0]) : aboutIds.map(ref),
    mentions: mentions.filter((id, index) => !aboutIds.includes(id) && mentions.indexOf(id) === index).map(ref),
    breadcrumb: hasBreadcrumb ? ref(breadcrumbId(path)) : undefined,
    datePublished,
    dateModified,
    lastReviewed,
    reviewedBy: personRef(reviewedBy),
    author: personRef(author)
  })
}

/**
 * FAQPage (PRD §54). MUST receive the very same array that is rendered by `<FaqAccordion>`.
 * Returns `undefined` for an empty list.
 */
export const buildFaqPage = (
  path: string,
  items: FaqItem[],
  partOfId: string = webPageId(path)
): JsonLdNode | undefined => {
  if (!items.length) return undefined

  return {
    '@type': 'FAQPage',
    '@id': faqId(path),
    url: canonicalUrl(path),
    inLanguage: MEDLEVE_LANGUAGE,
    isPartOf: ref(partOfId),
    mainEntity: items.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer }
    }))
  }
}

/**
 * BreadcrumbList (PRD §55). Pass the output of `resolveBreadcrumbs` / `getBreadcrumbsForPath` — the same
 * array given to `<Breadcrumbs>`. Crumbs without a live `path` are emitted without `item`.
 * Returns `undefined` when there is nothing to list.
 */
export const buildBreadcrumbList = (path: string, items: BreadcrumbItem[]): JsonLdNode | undefined => {
  if (!items.length) return undefined

  return {
    '@type': 'BreadcrumbList',
    '@id': breadcrumbId(path),
    itemListElement: items.map((item, index) =>
      compact({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.path ? canonicalUrl(item.path) : undefined
      })
    )
  }
}

/**
 * Person (PRD §45–47). Reused by `author` / `reviewedBy` through the same @id (PRD §46).
 * CRM goes in `identifier`; `role` → `jobTitle`; `specialty` → `knowsAbout`. RQE is pending confirmation.
 * Nothing is emitted for missing fields, and `education` is not mapped (free text has no exact property).
 */
export const buildPerson = (doctor: DoctorEntry): JsonLdNode =>
  compact({
    '@type': 'Person',
    '@id': personId(doctor.slug),
    name: doctor.name,
    honorificPrefix: doctor.honorificPrefix,
    jobTitle: doctor.role,
    knowsAbout: doctor.specialty,
    image: doctor.image ? absoluteUrl(doctor.image) : undefined,
    url: isPublishable(doctor) ? canonicalUrl(doctor.path) : undefined,
    identifier: [
      ...(doctor.crm
        ? [{ '@type': 'PropertyValue', propertyID: `CRM-${doctor.crm.uf}`, value: doctor.crm.number }]
        : [])
    ],
    sameAs: doctor.sameAs
  })

export interface ProfilePageOptions {
  name?: string
  description?: string
  hasBreadcrumb?: boolean
}

/**
 * ProfilePage with `mainEntity` → Person (PRD §45). It IS the page node of `/equipe-medica/[slug]`
 * (ProfilePage is a WebPage subtype), so no separate `#webpage` node is emitted for that URL.
 */
export const buildProfilePage = (
  doctor: DoctorEntry,
  { name, description, hasBreadcrumb = false }: ProfilePageOptions = {}
): JsonLdNode =>
  compact({
    '@type': 'ProfilePage',
    '@id': profileId(doctor.slug),
    url: canonicalUrl(doctor.path),
    name: name || [doctor.honorificPrefix, doctor.name].filter(Boolean).join(' '),
    description,
    inLanguage: MEDLEVE_LANGUAGE,
    isPartOf: ref(websiteId()),
    publisher: ref(organizationId()),
    mainEntity: ref(personId(doctor.slug)),
    breadcrumb: hasBreadcrumb ? ref(breadcrumbId(doctor.path)) : undefined,
    datePublished: doctor.review.datePublished,
    dateModified: doctor.review.dateModified
  })

/** Registry entries that have their own `#entity` node. */
export type GraphEntity = BrandEntry | MoleculeEntry

/**
 * Main-entity node for a brand or molecule (PRD §48/§49), identified by `<its page>#entity`.
 * - Brand → `Drug`, with the molecule as `activeIngredient`/`nonProprietaryName` (text, per schema.org).
 * - Molecule → `Substance`.
 * The two never share an @id. `url` is only present once the entity's own page is live.
 * REQUIRES MEDICAL REVIEW before any clinical property (indication, dose, mechanism…) is added here.
 */
export const buildEntityNode = (entry: GraphEntity): JsonLdNode => {
  const url = isPublishable(entry) ? canonicalUrl(entry.path) : undefined

  if (entry.kind === 'molecule') {
    return compact({ '@type': 'Substance', '@id': entityId(entry.path), name: entry.name, url })
  }

  const molecule = getMolecule(entry.activeIngredient)
  return compact({
    '@type': 'Drug',
    '@id': entityId(entry.path),
    name: entry.name,
    url,
    activeIngredient: molecule?.name,
    nonProprietaryName: molecule?.name
  })
}

export interface PageGraphInput {
  /** Page facts. `about` / `mentions` are set below from registry entries, never as loose ids. */
  page: Omit<WebPageInput, 'about' | 'mentions' | 'hasBreadcrumb'>
  /**
   * Main entity of the page (PRD §42/§49): a brand/molecule entry, two of them for a comparison, or
   * `'organization'` for pages about Medleve itself (homepage).
   */
  about?: GraphEntity | GraphEntity[] | 'organization'
  /** Secondary brand/molecule entries that the VISIBLE copy really mentions (PRD §31/§42). */
  mentions?: GraphEntity[]
  /** Same array rendered by `<FaqAccordion>` (PRD §54). */
  faq?: FaqItem[]
  /** Same array rendered by `<Breadcrumbs>` (PRD §55). */
  breadcrumbs?: BreadcrumbItem[]
  /** Doctor whose profile this page is: the page node becomes a ProfilePage. `page.path` must be the doctor's path. */
  profileOf?: DoctorEntry
}

/**
 * One coherent `@graph` per page (PRD §39–41): Organization + WebSite + WebPage (or ProfilePage), plus
 * BreadcrumbList, FAQPage, entity and Person nodes when applicable. Every `{ "@id": … }` reference
 * points to a node of the same graph: entity nodes come from `about`/`mentions`, Person nodes from
 * `page.reviewedBy`, `page.author` and `profileOf`. Nodes are de-duplicated by @id.
 */
export const buildPageGraph = ({
  page,
  about,
  mentions = [],
  faq = [],
  breadcrumbs = [],
  profileOf
}: PageGraphInput): JsonLdGraph => {
  const path = profileOf ? profileOf.path : page.path
  const aboutEntities = about === undefined || about === 'organization' ? [] : Array.isArray(about) ? about : [about]
  const aboutIds = about === 'organization' ? [organizationId()] : aboutEntities.map((entry) => entityId(entry.path))
  const breadcrumbList = buildBreadcrumbList(path, breadcrumbs)
  const hasBreadcrumb = !!breadcrumbList

  const pageNode = profileOf
    ? buildProfilePage(profileOf, { name: page.name, description: page.description, hasBreadcrumb })
    : buildWebPage({
        ...page,
        about: aboutIds.length === 1 ? aboutIds[0] : aboutIds,
        mentions: mentions.map((entry) => entityId(entry.path)),
        hasBreadcrumb
      })

  const people = [profileOf, getDoctor(page.reviewedBy ?? ''), getDoctor(page.author ?? '')].filter(
    (doctor): doctor is DoctorEntry => !!doctor
  )

  const nodes: Array<JsonLdNode | undefined> = [
    buildOrganization(),
    buildWebSite(),
    pageNode,
    breadcrumbList,
    buildFaqPage(path, faq, pageNode['@id']),
    ...[...aboutEntities, ...mentions].map(buildEntityNode),
    ...people.map(buildPerson)
  ]

  const seen = new Set<string>()
  const graph = nodes.filter((node): node is JsonLdNode => {
    if (!node || seen.has(node['@id'])) return false
    seen.add(node['@id'])
    return true
  })

  return { '@context': 'https://schema.org', '@graph': graph }
}
